import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './MobileProfileOverview.styled';
import {
  getMobileProfileMenuItems,
  getMobileProfileQuickNavItems,
} from '@/features/Profile/core/ProfileNav/ProfileNav.constants';
import { isProfileNavItemActive } from '@/features/Profile/core/ProfileNav/ProfileNav.helpers';
import { ShopAccountSummary } from '@/features/Profile/core/ShopAccountSummary';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  CloseIcon,
  ExitIcon,
  MoreHorizontalIcon,
} from '@/components/UI/Icons/Icons';
import { CopyButton } from '@/components/CopyButton';
import { formatNumber, formatPhoneNumber } from '@/utils/helpers';
import { useAuth } from '@/hooks/useAuth';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import {
  useBusinessBalance,
  useCurrentUser,
} from '@/features/Profile/core/hooks';
import { AddBalance } from '@/components/Payment/AddBalance';
import NoSsr from '@/components/NoSsr/NoSsr';
import { WarningModal } from '@/components/Ad/WarningModal';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { QUERY } from '@/constants/common';

type MobileProfileOverviewProps = {
  currentPath: string;
};

export const MobileProfileOverview = ({
  currentPath,
}: MobileProfileOverviewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAddBalanceOpen, toggleAddBalance] = useToggleOpenState(
    QUERY.replenishment
  );
  const [isSuccessPopupOpen, toggleSuccessPopup] = useToggleOpenState(false);
  const { isActiveShop, isShop, loading, shop, user } = useCurrentUser();
  const { logoutUser } = useAuth();
  const menuItems = getMobileProfileMenuItems(t, isShop);
  const quickNavItems = getMobileProfileQuickNavItems(t, isShop);
  const { refetchBalance } = useBusinessBalance();

  const name = isActiveShop && shop ? shop.name : user.name;
  const avatarUrl = isActiveShop && shop ? shop.logo : user.avatarUrl;
  const {
    id,
    phone,
    email,
    emailConfirmed,
    walletBalance,
    isAppleGooglePayEnabled,
    paymentMethods,
  } = user;

  function openMenu() {
    setIsMenuOpen(true);
  }

  function closeMenu() {
    setIsMenuOpen(false);
  }

  function handleLogout() {
    logoutUser();
  }

  function openSuccessPopup() {
    toggleSuccessPopup(true);
  }

  return (
    <S.Container>
      <S.UserRow>
        <S.Avatar $avatarUrl={avatarUrl} />
        <S.UserInfo>
          {name && <S.UserName>{name}</S.UserName>}
          <S.UserMeta>
            {id && (
              <S.UserId>
                ID: {id}
                <CopyButton
                  value={id}
                  successText={t('pages.cabinet.profile_number_copied')}
                />
              </S.UserId>
            )}
            {phone && <S.Phone> - {formatPhoneNumber(phone)}</S.Phone>}
          </S.UserMeta>
        </S.UserInfo>
        <S.MenuButton
          type="button"
          onClick={openMenu}
          aria-label="Open profile menu"
        >
          <S.MenuButtonIconOutline>
            <MoreHorizontalIcon width={12} fill="currentColor" />
          </S.MenuButtonIconOutline>
        </S.MenuButton>
      </S.UserRow>

      <S.EmailConfirmationLinkBox
        email={email}
        emailConfirmed={emailConfirmed}
      />

      {isShop ? (
        <ShopAccountSummary
          balance={walletBalance ?? 0}
          isActiveShop={isActiveShop}
          isBalanceLoading={loading}
          onAddBalance={() => toggleAddBalance()}
        />
      ) : (
        <S.Balance>
          <div>
            <S.BalanceLabel>
              {t('pages.cabinet.private_account')}
            </S.BalanceLabel>
            {loading ? (
              <SkeletonLoader
                appearance="primary"
                width={80}
                height={22}
                borderRadius={7}
              />
            ) : (
              <S.BalanceValue>
                {formatNumber(walletBalance, 2).replace(/\s/g, '')} ₼
              </S.BalanceValue>
            )}
          </div>
          <S.BalanceButton
            appearance="secondary"
            borderRadius={14}
            onClick={() => toggleAddBalance()}
          >
            {t('pages.cabinet.add')}
          </S.BalanceButton>
        </S.Balance>
      )}

      <S.QuickNav
        aria-label="Profile quick navigation"
        $itemCount={quickNavItems.length}
      >
        {quickNavItems.map(({ href, icon, title }) => (
          <S.QuickNavItem key={href} href={href}>
            <S.QuickNavIcon>{icon}</S.QuickNavIcon>
            <S.QuickNavTitle>{title}</S.QuickNavTitle>
          </S.QuickNavItem>
        ))}
      </S.QuickNav>

      <Drawer
        isOpen={isMenuOpen}
        close={closeMenu}
        title={t('common.menu')}
        left={{ icon: <CloseIcon />, onClick: closeMenu }}
        background={theme.palette.primary.background}
        hasBottomMargin={false}
        hasBottomBorder={false}
      >
        <S.MenuContent>
          <S.MenuGrid aria-label="Profile menu">
            {menuItems.map(({ href, icon, title }) => (
              <S.MenuItem
                key={href}
                href={href}
                $isActive={isProfileNavItemActive(currentPath, href)}
                onClick={closeMenu}
              >
                {icon}
                <S.MenuItemTitle>{title}</S.MenuItemTitle>
              </S.MenuItem>
            ))}
          </S.MenuGrid>

          <S.MenuDivider />

          <S.MenuLogoutButton type="button" onClick={handleLogout}>
            <ExitIcon />
            <S.ExitButtonTitle>{t('common.exit')}</S.ExitButtonTitle>
          </S.MenuLogoutButton>
        </S.MenuContent>
      </Drawer>

      <NoSsr>
        <AddBalance
          isOpen={isAddBalanceOpen}
          toggle={toggleAddBalance}
          isAppleGooglePayEnabled={isAppleGooglePayEnabled}
          onSuccess={openSuccessPopup}
          paymentMethods={paymentMethods}
          isInPortal
          {...(isShop && {
            onBalanceTopUpSuccess: () => {
              refetchBalance();
            },
          })}
        />
      </NoSsr>

      <WarningModal
        dataTestId={AdPageTestIds.popup.success}
        isOpen={isSuccessPopupOpen}
        onClose={() => toggleSuccessPopup(false)}
        icon="check-circle-rounded.svg"
        iconSize={30}
        title={t('pages.cabinet.add_balance_success_message')}
        description=""
      />
    </S.Container>
  );
};
